import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Camera, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const CATS = ["plumbing", "electrical", "security", "housekeeping", "parking", "water", "lift", "noise", "vendor", "admin", "suggestion", "other"];

function VoicePanel({ transcription, aiBusy, keepAudio, onKeepAudioChange, onTranscript }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 flex items-center gap-5">
      <VoiceRecorder onTranscript={onTranscript} testid="issue-voice" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Voice note</div>
        <div className="text-xs text-stone-500 mt-0.5">Hindi, Kannada, Tamil, Telugu, Malayalam or English. Auto-transcribed and triaged by AI.</div>
        {transcription && (
          <div className="text-xs text-stone-700 italic mt-2 bg-[#FDFBF7] rounded-lg p-2 line-clamp-3">"{transcription}"</div>
        )}
        {aiBusy && <div className="text-xs text-[#C05640] mt-2 inline-flex items-center gap-1"><Sparkles size={12} /> AI is classifying…</div>}
        {transcription && (
          <label className="inline-flex items-center gap-2 mt-3 text-xs text-stone-600">
            <input type="checkbox" checked={keepAudio} onChange={(e) => onKeepAudioChange(e.target.checked)} data-testid="keep-audio" className="accent-[#C05640]" />
            Keep audio recording (auto-deletes after retention period)
          </label>
        )}
      </div>
    </div>
  );
}

function PhotoPicker({ photos, onAdd, onRemove }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2 items-center">
      {photos.map((p) => (
        <span key={p.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-stone-100 text-xs">
          {p.original_filename}
          <button type="button" onClick={() => onRemove(p.id)} className="text-stone-400 hover:text-red-600"><X size={12} /></button>
        </span>
      ))}
      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-sm hover:border-stone-500" data-testid="issue-photo-btn">
        <Camera size={16} /> Add photo
        <input type="file" accept="image/*" capture="environment" hidden onChange={onAdd} />
      </label>
    </div>
  );
}

export default function IssueCreatePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [isCommon, setIsCommon] = useState(true);
  const [transcription, setTranscription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [keepAudio, setKeepAudio] = useState(user?.role === "staff" || !!user?.audio_default_keep);
  const [assignTo, setAssignTo] = useState("");
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (user?.community_id) {
      api.get(`/communities/${user.community_id}/users`).then((r) => setMembers(r.data));
    }
  }, [user?.community_id]);

  async function handleVoice(text) {
    setTranscription(text);
    if (!title) setTitle(text.split(".")[0].slice(0, 80));
    if (!description) setDescription(text);
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/categorize", { text });
      setCategory(data.category || "other");
      if (data.severity) setPriority(data.severity === "urgent" ? "urgent" : data.severity);
    } catch (e) { console.error("AI categorize failed", e); }
    finally { setAiBusy(false); }
  }

  async function handlePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      setPhotos((p) => [...p, data]);
      toast.success("Photo attached");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  function removePhoto(id) { setPhotos((p) => p.filter((x) => x.id !== id)); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title, description, category: category || "other", priority,
        is_common_area: isCommon, attachments: photos,
        audio_transcription: transcription || null,
        keep_audio: keepAudio,
      };
      const { data } = await api.post("/issues", payload);
      if (assignTo) {
        await api.patch(`/issues/${data.id}`, { assigned_to: assignTo, status: "assigned" });
      }
      toast.success(`Issue ${data.number} raised`);
      nav(`/issues/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">New issue</div>
      <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Tell us what's wrong</h1>
      <p className="text-stone-600 text-sm mt-1">Speak it, type it, or snap a photo. We'll structure it for you.</p>

      <form onSubmit={submit} className="mt-7 space-y-5" data-testid="issue-form">
        <VoicePanel transcription={transcription} aiBusy={aiBusy} keepAudio={keepAudio}
          onKeepAudioChange={setKeepAudio} onTranscript={handleVoice} />

        <div>
          <Label>Title</Label>
          <Input value={title} required onChange={(e) => setTitle(e.target.value)} data-testid="issue-title" className="h-12 mt-1.5" placeholder="What is the issue?" />
        </div>
        <div>
          <Label>Details (optional)</Label>
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="issue-desc" className="mt-1.5" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="issue-cat"
              className="w-full h-12 mt-1.5 rounded-xl border border-stone-300 bg-white px-3">
              <option value="">Select…</option>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Priority</Label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="issue-priority"
              className="w-full h-12 mt-1.5 rounded-xl border border-stone-300 bg-white px-3">
              {["low", "medium", "high", "urgent"].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {user?.role === "committee" && (
          <div>
            <Label>Assign to (optional)</Label>
            <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="issue-assign"
              className="w-full h-12 mt-1.5 rounded-xl border border-stone-300 bg-white px-3">
              <option value="">— Unassigned —</option>
              {members.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        )}

        <div className="flex items-center gap-3 bg-[#F4F1EA] rounded-xl p-4">
          <input type="checkbox" id="common" checked={isCommon} onChange={(e) => setIsCommon(e.target.checked)} data-testid="issue-common" className="w-5 h-5 accent-[#C05640]" />
          <label htmlFor="common" className="text-sm">This is a <strong>common area</strong> issue (not specific to one flat)</label>
        </div>

        <div>
          <Label>Photos</Label>
          <PhotoPicker photos={photos} onAdd={handlePhoto} onRemove={removePhoto} />
        </div>

        <Button type="submit" disabled={busy} data-testid="issue-submit"
          className="w-full h-12 rounded-full bg-[#C05640] hover:bg-[#A54734] text-base font-semibold">
          {busy ? "Submitting…" : "Submit issue"}
        </Button>
      </form>
    </div>
  );
}
